'use client';

import { forwardRef, useEffect, useId, useRef, useState } from 'react';
import { cn } from '../../lib/utils/classnameVariants';
import { FormLabel } from '../atoms/FormLabel';
import { Input } from '../atoms/Input';
import { Description } from './TextField';

export interface NumberFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'icon' | 'inputMode'
> {
  label: string;
  styleVariant?: 'default' | 'border';
  helpText?: string;
  optionalText?: string;
  validated?: boolean;
  error?: string;
  loading?: boolean;
  /** Display with dot as thousands separator (e.g. 1.234.567) when blurred. */
  formatThousands?: boolean;
  /** Allow decimal numbers. Both comma (,) and dot (.) are accepted; dot is used internally, comma shown when formatted */
  allowDecimals?: boolean;
  /**
   * Controls which sign values are accepted.
   * - `'positive-only'` — only values > 0
   * - `'any'` — any value including negative
   * - omitted (default) — zero and positive (>= 0)
   */
  sign?: 'positive-only' | 'any';
}

const NumberField = forwardRef<HTMLDivElement, NumberFieldProps>(
  (
    {
      label,
      helpText,
      optionalText,
      error,
      loading,
      disabled,
      formatThousands,
      className,
      id,
      sign,
      allowDecimals,
      onChange,
      value,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const fieldId = id ?? generatedId;
    const [isFocused, setIsFocused] = useState(false);
    const helpTextId = useId();
    const isFocusedRef = useRef(false);
    isFocusedRef.current = isFocused;

    const rawValue = (() => {
      if (value === undefined || value === null) return '';
      if (typeof value === 'number') return Number.isNaN(value) ? '' : String(value);
      if (Array.isArray(value)) return value[0] ?? '';
      if (typeof value === 'string') return Number.isNaN(Number(value)) ? '' : value;
      return String(value);
    })();

    const [localValue, setLocalValue] = useState(rawValue);

    useEffect(() => {
      if (!isFocusedRef.current) {
        setLocalValue(rawValue);
      }
    }, [rawValue]);

    // Format for display when blurred: dot as thousands separator, comma as decimal.
    const formatBlurred = (val: string): string => {
      if (!val || val === '-') return val;
      const negative = val.startsWith('-');
      const abs = negative ? val.slice(1) : val;
      const [intPart = '', decPart] = abs.split('.');
      const formattedInt = formatThousands ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.') : intPart;
      const result = decPart !== undefined ? `${formattedInt},${decPart}` : formattedInt;
      return negative ? `-${result}` : result;
    };

    const displayValue = isFocused ? localValue : formatBlurred(localValue);
    const errorMessageId = `${fieldId}-error`;

    const effectiveSign = sign;

    // Returns true if normalized (dot-decimal) string is a valid intermediate or final value.
    const isAllowed = (normalized: string): boolean => {
      if (normalized === '') return true;
      if (normalized === '-') return effectiveSign === 'any';

      const signPattern = effectiveSign === 'any' ? '-?' : '';
      const int = '(0|[1-9]\\d*)';
      const dec = allowDecimals ? '(\\.\\d*)?' : '';
      if (!new RegExp(`^${signPattern}${int}${dec}$`).test(normalized)) return false;

      if (effectiveSign === 'positive-only') {
        // Trailing dot is a valid intermediate (e.g. "1." while typing "1.5")
        const n = Number(normalized);
        if (!normalized.endsWith('.') && !Number.isNaN(n) && n <= 0) return false;
      }

      return true;
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      props.onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      props.onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      const normalized = val.replace(',', '.');

      if (!isAllowed(normalized)) return;

      // localValue always stores normalized so React's value prop matches DOM, preventing cursor jumps.
      setLocalValue(normalized);

      if (normalized !== val) {
        // Comma was typed — fix event value for parent and restore cursor position.
        const { selectionStart, selectionEnd } = e.target;
        e.target.value = normalized;
        e.target.setSelectionRange(selectionStart, selectionEnd);
      }

      onChange?.(e);
    };

    const hasHelpText = !!helpText && (!error || isFocused);
    const describedBy =
      [props['aria-describedby'], error && errorMessageId, hasHelpText && helpTextId].filter(Boolean).join(' ') ||
      undefined;

    return (
      <div className={className} ref={ref}>
        <div className={cn(disabled && 'opacity-50', 'flex items-start justify-between')}>
          {label && <FormLabel title={label} />}
          {optionalText && <p className="my-auto font-graphik text-xs font-normal text-black-300">{optionalText}</p>}
        </div>
        <div className={cn(disabled && 'opacity-50', 'my-2')}>
          <Input
            {...props}
            id={fieldId}
            aria-describedby={describedBy}
            aria-invalid={error ? true : props['aria-invalid']}
            inputMode={allowDecimals ? 'decimal' : 'numeric'}
            label={label}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            loading={loading}
            type="text"
            value={displayValue}
            onChange={handleChange}
            error={!isFocused && !!error}
            validated={!isFocused && !!props.validated}
            className={disabled ? 'cursor-not-allowed' : ''}
          />
        </div>
        {error && <Description id={errorMessageId} role="alert" isError description={error} />}
        {hasHelpText && <Description id={helpTextId} description={helpText} />}
      </div>
    );
  },
);

NumberField.displayName = 'NumberField';

export default NumberField;
